import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { LocalStorageService } from "src/app/utils/localstorage.service";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { HotToastService } from "@ngneat/hot-toast";

import { Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { Conversation } from "../conversations/panels/conversation.model";
import { DashboardModule } from "../dashboard.module";
import { DashboardService } from "../dashboard.service";
import { AddDefaultFoldersModal } from "../main/accountant/modals/addDefaultFoldersConfirmationModal";
import { DeleteConfirmationModalComponent } from "../main/accountant/modals/delete-confirmation-modal.component";
import { FileUploadModalComponent } from "../main/accountant/modals/file-upload-modal.component";
import { MoveFileModalComponent } from "../main/accountant/modals/move-file-modal.component";
import { WorkspacesService } from "../main/accountant/workspaces.service";
import { FilePreviewService } from "../main/common/file-preview.service";
import { FilesManagerService } from "./files-manager.service";
import { RootFolders } from "./types";
import { SignRequestModalComponent } from "./sign-request/sign-request-modal.component";
import { GenericDrawerService } from "../drawer/global-drawer.service";
import { SignPreviewComponent } from "./sign-preview/sign-preview.component";
import { SecureDeleteConfirmationModalComponent } from "../main/accountant/modals/secure-delete-confirmation-modal.component";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { GroupByPipe } from "src/app/dashboard/files-manager/groupby.pipe";
import { CanComponentDeactivate } from "src/app/unsaved-changes.guard";
import { FilesTableViewComponent } from "./files-table-view/files-table-view.component";

export const FOLDER_INDEX = {
  NONE: 0,
  MESSAGE_FILES: 1,
  TRACKED_ITEM_FILES: 2,
  CLIENT_SHARED_FILES: 3,
  STAFF_ONLY_FILES: 4,
  TRASH: 5,
  E_SIGNATURE_FILES: 11,
  EASY_UPLOAD_FILES: 12,
};

@Component({
  selector: "app-files-manager",
  templateUrl: "./files-manager.component.html",
  imports: [
    CommonModule,
    FormsModule,
    DashboardModule,
    BsDropdownModule,
    TooltipModule,
    GroupByPipe,
    FilesTableViewComponent,
  ],
  styleUrls: ["./files-manager.component.scss"],
})
export class FilesManagerComponent implements OnInit, CanComponentDeactivate {
  @ViewChildren("fileElement") fileElements: QueryList<ElementRef>;
  // Modals
  bsModalRef: BsModalRef;
  bsDeleteModalRef: BsModalRef;
  bsFileUploadModalRef: BsModalRef;
  bsSignRequestModalRef: BsModalRef;

  is_loading = false;
  preSelectLoading = false;

  // folders and files
  recentFiles: any = [];

  openedFile: any;

  navigationViewVisible = false;
  renamingFile = false;
  newFolderVisible = false;
  renameFolderVisible = false;

  btnFolderBackVisible = false;
  btnFolderOperationsVisible = false;

  search: any = "";

  folderDetailsViewVisible = false;
  fileViewVisible = false;
  fileDetails = {
    filename: "",
    ext: "",
    upload_date: "",
    url: "",
    user: { full_name: "" },
    etag: "",
  };
  fileImageExtensions = ["jpg", "jpeg", "gif", "png", "bmp"];

  fileCounters = {
    all: 0,
    messages: 0,
    tracked: 0,
    staff: 0,
    trash: 0,
    eSign: 0,
    easyUpload: 0,
  };

  folderCounters = {
    all: 0,
    messages: 0,
    tracked: 0,
    staff: 0,
    trash: 0,
  };

  currentFolder: any = []; // the folder that is currently open (i.e middle column)
  breadcrumbs = {
    folders: [
      {
        name: "Home", // name of the folder
        uuid: "", // id of the folder - used to fetch its info again when needed
        data: [], // exactly same data as currentFolder (and previousFolder)
      },
    ],
  };

  moment_calendar_format = {
    sameDay: "[Today]",
    nextDay: "[Tomorrow]",
    nextWeek: "dddd",
    lastDay: "[Yesterday]",
    lastWeek: "[Last] dddd",
    sameElse: "MMM D, YYYY",
  };

  filePluralMapping = {
    "=0": "No files",
    "=1": "1 file",
    other: "# files",
  };

  folderPluralMapping = {
    "=0": "No folders",
    "=1": "1 folder",
    other: "# folders",
  };

  requestPluralMapping = {
    "=0": "No sign requests",
    "=1": "1 sign request",
    other: "# sign requests",
  };

  selectedRecentFileIndex = 0;
  selectedFolderIndex = 0;
  selectedFileIndex = 0;

  staffsFiles = [];
  clientsFiles = [];
  signatureRequests = [];

  current_conversation: Conversation;
  current_company: any = { users: [], accountants: [], owner: null, notes: "" };
  current_data: any = { user_type: 1 };

  recentFilesControl: Subscription;

  selectedFileObservable = null;

  bulkActionSelectedFiles = [];
  bulkActionSelectedFolders = [];

  showSignatureRequestDetails = false;
  selectedSignatureRequest: {id: number, conversation: number, request_name?: string} = null;

  clientSharedFolderUuid = RootFolders.ROOT_CLIENT_SHARED;
  staffOnlyFolderUuid = RootFolders.ROOT_STAFF_ONLY;

  // Drag and Drop Upload
  isDraggingFileOverCurrentFolder = false;
  isDraggingFileOverSubfolder = false;
  uploadFolderName = this.currentFolder.title;
  uploadListCollapsed = false;
  uploadList: any[] = [];
  totalUploadedFiles = 0;
  uploadListOpen = false;
  uploadErrors = 0;
  warningUploadInProgress = false;

  canDeactivate(): boolean {
    if (this.warningUploadInProgress) {
      return confirm(
        "An upload is in progress. Are you sure you want to leave?",
      );
    }
    return true; // Allow navigation if no warning is needed
  }

  orderBy = "date"; // options are ["date", "-date", "name"]
  orderByLabel = "Date (Oldest)";

  lastFolderRetrieved = undefined;
  folderIndexMapping = {
    1: "getMessagesFiles",
    2: "getTrackedItemsFiles",
    3: "getClientShared",
    4: "getStaffOnly",
    11: "getSignatureRequests",
    12: "getEasyUploadFiles",
  };

  // Different views
  view = "folders";
  listFiltering = "from-client";

  @ViewChild("txtNewFolder") txtNewFolder: ElementRef;

  private subscriptions = new Subscription();

  constructor(
    public dashboardService: DashboardService,
    public toastr: HotToastService,
    private filesManagerService: FilesManagerService,
    private modalService: BsModalService,
    private workspacesService: WorkspacesService,
    public localStorage: LocalStorageService,
    private renderer: Renderer2,
    private elem: ElementRef,
    private filePreviewService: FilePreviewService,
    private genericDrawerService: GenericDrawerService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.current_data = this.localStorage.get("data");
    this.loadUserPreferences();

    this.subscriptions.add(
      this.workspacesService.$conversation.subscribe((conversation) => {
        if (conversation != null) {
          this.current_conversation = conversation;
          this.initializeFilesManager();
          this.getClientShared();
        }
      }),
    );

    this.subscriptions.add(
      this.workspacesService.$company.subscribe((company) => {
        if (company != null) {
          this.current_company = company;
        }
      }),
    );

    this.selectedFileObservable =
      this.workspacesService.notificationsSelectedFileObservable.subscribe(
        (etag: string) => {
          if (etag) {
            this.getSelectedFileAndFolderTree(etag);
          }
        },
      );

    this.subscriptions.add(
      this.filesManagerService.fileChanged.subscribe((change) => {
        if (change.action == "rename") {
          this.reload();
          this.getRecentFiles();
        }

        if (change.action == "delete") {
          this.reload();
          this.loadFilesManagerHome();
          // file deleted, dont select anything
          this.selectedFileIndex = 0;
        }

        if (change.action == "move") {
          // open the new target folder
          if (change.target_uuid === "client") this.getClientShared();
          else if (change.target_uuid === "staff") this.getStaffOnly();
          else this.getFolder(change.target_uuid);
          // refresh counters
          this.getFileCounters();
        }
      }),
    );

    this.subscriptions.add(
      this.genericDrawerService.closeDrawer$.subscribe(() => {
        this.loadFilesManagerHome();
        this.getSignatureRequests();
      }),
    );
  }

  private loadUserPreferences() {
    this.orderBy = this.localStorage.get("preferredOrderBy") ?? this.orderBy;
    this.orderByLabel =
      this.localStorage.get("preferredOrderByLabel") ?? this.orderByLabel;
    this.view = this.localStorage.get("preferredView") ?? this.view;
    this.listFiltering =
      this.localStorage.get("preferredListFiltering") ?? this.listFiltering;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (this.selectedFileObservable != null) {
      this.selectedFileObservable.unsubscribe();
    }
  }

  isObjectEmpty(obj: any) {
    for (var i in obj) return false;
    return true;
  }

  addDefaultFolders(conversation_id: Number): void {
    this.filesManagerService
      .addDefaultFolders(conversation_id)
      .subscribe((res) => {
        this.reload();
        this.toastr.success("Default foulders successfully added");
      });
  }

  openAddDefaultFoldersConfirmationModal() {
    const addDefaultFoldersModal = this.modalService.show(
      AddDefaultFoldersModal,
    );
    addDefaultFoldersModal.content.onClose.subscribe((confirmed) => {
      if (confirmed) {
        this.addDefaultFolders(this.current_conversation.id);
      }
    });
  }

  getSelectedFileAndFolderTree(etag: string) {
    if (!etag || etag === this.fileDetails?.etag) {
      return;
    }
    this.preSelectLoading = true;

    this.filesManagerService
      .getNavigatedFile(this.current_conversation.id, etag)
      .subscribe(
        (res: any) => {
          this.fileDetails = res.file;
          if (res.breadcrumbs && res.breadcrumbs.length > 0) {
            this.resetBreadcrumbs();
            res.breadcrumbs.forEach((bc: any) =>
              this.appendBreadcrumb(bc.name, bc.uuid, bc.data),
            );
          }
          if (!this.isObjectEmpty(res.folder)) {
            this.currentFolder = res.folder;
            this.navigationViewVisible = true;
            this.btnFolderBackVisible = true;
            this.btnFolderOperationsVisible = true;
            this.highlightFileInFolder();
          } else {
            if (res.file.staff_only && this.current_data.user_type === 1) {
              this.getStaffOnly();
            } else {
              this.getClientShared();
            }
          }
          this.fileViewVisible = true;
          this.preSelectLoading = false;
        },
        (err) => {
          this.preSelectLoading = false;
          this.toastr.error(err.error.message);
        },
      );
  }

  highlightFileInFolder() {
    if (!this.fileDetails) {
      return;
    }
    try {
      const arrIdx = this.currentFolder.files.indexOf(
        this.currentFolder.files.find(
          (o: any) => o.etag === this.fileDetails.etag,
        ),
      );
      this.selectedFileIndex = arrIdx + 1;
    } catch {
      return;
    }
  }

  public openModalFileUpload() {
    this.bsFileUploadModalRef = this.modalService.show(
      FileUploadModalComponent,
      {
        initialState: {
          currentConversationID: this.current_conversation.id,
          targetFolder: this.currentFolder.uuid,
          staff: Boolean(this.selectedFolderIndex === 4),
        },
      },
    );
    this.bsFileUploadModalRef.content.onClose.subscribe(() => {
      this.reload();
      this.loadFilesManagerHome();
    });
  }

  public openSignatureRequestModal() {
    this.bsSignRequestModalRef = this.modalService.show(
      SignRequestModalComponent,
      {
        initialState: {
          currentConversationID: this.current_conversation.id,
        },
      },
    );
    this.bsSignRequestModalRef.content.onComplete.subscribe((res: any) => {
      this.bsSignRequestModalRef.hide();

      this.genericDrawerService.changeComponent(SignPreviewComponent, {
        mode: res.status,
        signRequest: res,
      });
    });

    this.bsSignRequestModalRef.content.onClose.subscribe(() => {
      this.loadFilesManagerHome();
      this.getSignatureRequests();
    });
  }

  getFolderDeletionMessage(): string {
    const folderQty = this.currentFolder.folders.length;
    const filesQty = this.currentFolder.files.length;
    const folderTitle = this.currentFolder.title;

    let message = `Are you sure you want to delete the folder <strong>${folderTitle}</strong>`;

    if (folderQty > 0) {
      message += ` AND ALL the <strong>${folderQty}</strong> `;
      message += folderQty === 1 ? "folder" : "folders";
    }

    if (filesQty > 0) {
      if (folderQty > 0) {
        message += ` and `;
      } else {
        message += ` AND ALL the `;
      }
      message += `<strong>${filesQty}</strong> `;
      message += filesQty === 1 ? "file" : "files";
    }

    if (folderQty || filesQty) {
      message += " within it";
    }
    message += "?";
    return message;
  }

  public openDeleteConfirmationModal() {
    const initialState = {
      deletionMessage: this.getFolderDeletionMessage(),
    };

    const deleteModal = this.modalService.show(
      SecureDeleteConfirmationModalComponent,
      {
        initialState,
      },
    );
    deleteModal.content.onClose.subscribe((confirmed) => {
      if (confirmed) {
        this.deleteFolder(this.currentFolder.uuid);
      }
    });
  }

  openModalForCutOrCopyFoldersAndFiles(action: string) {
    const initialState = {
      cutOrCopyAction: true,
      currentConversationID: this.current_conversation.id,
      folderDetails: {
        folder_name: this.currentFolder.name,
        uuid: this.currentFolder.uuid,
      },
      selectedFolders: this.bulkActionSelectedFolders,
    };

    const moveFilesAndFoldersModal = this.modalService.show(
      MoveFileModalComponent,
      {
        initialState,
      },
    );

    moveFilesAndFoldersModal.content.onClose.subscribe(
      ({ target_uuid, selectedWorkspace }) => {
        // target_uuid can be false if the modal was just closed
        if (!target_uuid) {
          moveFilesAndFoldersModal.hide();
        }

        const promises = [];

        if (action === "copy") {
          promises.push(
            this.filesManagerService.copyFilesAndFolders(
              this.current_conversation.id,
              this.bulkActionSelectedFiles,
              this.bulkActionSelectedFolders,
              target_uuid,
            ),
          );
        }

        if (action === "cut") {
          if (this.bulkActionSelectedFolders.length > 0) {
            promises.push(
              this.filesManagerService.moveFolder(
                this.current_conversation.id,
                this.bulkActionSelectedFolders,
                target_uuid,
              ),
            );
          }

          if (this.bulkActionSelectedFiles.length > 0) {
            const payload: any = {
              folder: target_uuid,
              multiple: true,
              files: this.bulkActionSelectedFiles,
            };

            promises.push(
              this.filesManagerService.moveFile(
                this.current_conversation.id,
                payload,
              ),
            );
          }
        }

        Promise.all(promises)
          .then((results) => {
            if (action === "cut") {
              this.currentFolder.files = this.currentFolder.files.filter(
                (file) => !this.bulkActionSelectedFiles.includes(file.etag),
              );
            }

            this.bulkActionSelectedFolders = [];
            this.bulkActionSelectedFiles = [];
            moveFilesAndFoldersModal.hide();
            this.toastr.success(
              action === "cut"
                ? `Archives successfully moved`
                : "Archives successfully copied",
            );
            this.selectedFileIndex = 0;
            if (target_uuid === "client") this.getClientShared();
            else if (target_uuid === "staff") this.getStaffOnly();
            else this.getFolder(target_uuid);
          })
          .catch((error) => {
            console.log("move failed", error);
            moveFilesAndFoldersModal.hide();
            this.toastr.error("Something went wrong when moving the archives.");
          });
      },
    );
  }

  public openModalMoveFolder() {
    const initialState = {
      currentConversationID: this.current_conversation.id,
      folderDetails: {
        folder_name: this.currentFolder.name,
        uuid: this.currentFolder.uuid,
      },
    };
    const moveFolderModal = this.modalService.show(MoveFileModalComponent, {
      initialState,
    });

    moveFolderModal.content.onClose.subscribe(
      ({ target_uuid, selectedWorkspace }) => {
        // target_uuid can be false if the modal was just closed
        if (!target_uuid) {
          moveFolderModal.hide();
        }

        this.filesManagerService
          .moveFolder(
            this.current_conversation.id,
            [this.currentFolder.uuid],
            target_uuid,
          )
          .then(
            (result) => {
              console.log("moved successfully");
              moveFolderModal.hide();
              this.toastr.success(
                `Folder ${this.currentFolder.name} successfully moved`,
              );

              // file was moved, dont select anything
              this.selectedFileIndex = 0;
              // refresh current open folder
              // this.reload();
              // refresh counters
              // this.getFileCounters();
              this.initializeFilesManager();
            },
            (error) => {
              console.log("move failed", error);
              moveFolderModal.hide();
              this.toastr.error("Something went wrong when moving the folder.");
            },
          );
      },
    );
  }

  public openModalMoveFile(multiple?: boolean) {
    const initialState = {
      currentConversationID: this.current_conversation.id,
      fileDetails: {
        filename: this.fileDetails.filename,
        etag: this.fileDetails.etag,
      },
    };

    const moveFileModal = this.modalService.show(MoveFileModalComponent, {
      initialState,
    });
    moveFileModal.content.onClose.subscribe((target_uuid: any) => {
      // target_uuid can be false if the modal was just closed
      if (!target_uuid) {
        moveFileModal.hide();
      }
      const payload: any = {
        folder: target_uuid,
      };

      if (multiple) {
        payload.multiple = true;
        payload.files = this.bulkActionSelectedFiles;
      } else {
        payload.file = this.fileDetails.etag;
      }

      this.filesManagerService
        .moveFile(this.current_conversation.id, payload)
        .then(
          (result) => {
            moveFileModal.hide();
            this.toastr.success(`File successfully moved`);

            // file was moved, dont select anything
            this.selectedFileIndex = 0;
            // clean multiple selections as well
            this.bulkActionSelectedFiles = [];
            // refresh current open folder
            this.reload();
            // refresh counters
            this.getFileCounters();
          },
          (error) => {
            console.log("move failed", error);
            moveFileModal.hide();
            this.toastr.error("Something went wrong when moving the file.");
          },
        );
    });
  }

  public openModalDeleteFile(template) {
    this.bsDeleteModalRef = this.modalService.show(template);
  }

  public openRenameFile() {
    this.renamingFile = !this.renamingFile;
  }

  public openFolderForm() {
    this.newFolderVisible = !this.newFolderVisible;

    if (this.newFolderVisible) {
      setTimeout(() => {
        this.txtNewFolder.nativeElement.focus();
      }, 100);
    } else {
      this.txtNewFolder.nativeElement.value = "";
    }
  }

  //   _____.__.__                  ___.                 .__         .__
  // _/ ____\__|  |   ____   ______ \_ |__   ____   ____ |__| ____   |  |__   ___________   ____
  // \   __\|  |  | _/ __ \ /  ___/  | __ \_/ __ \ / ___\|  |/    \  |  |  \_/ __ \_  __ \_/ __ \
  //  |  |  |  |  |_\  ___/ \___ \   | \_\ \  ___// /_/  >  |   |  \ |   Y  \  ___/|  | \/\  ___/
  //  |__|  |__|____/\___  >____  >  |___  /\___  >___  /|__|___|  / |___|  /\___  >__|    \___  >
  //                     \/     \/       \/     \/_____/         \/       \/     \/            \/

  // one-time only (when building the screen)

  initializeFilesManager() {
    // hook to be used when we are refreshing everything
    // use cases: switching workspaces, opening File Manager

    // load Recent Files and the counter of Home folders
    this.loadFilesManagerHome();

    // close middle column
    this.navigationViewVisible = false;

    // close third column
    this.fileViewVisible = false;
    this.folderDetailsViewVisible = false;

    this.selectedFileIndex = 0;
    this.selectedFolderIndex = 0;

    // reset breadcrumbs
    this.currentFolder = [];
    this.breadcrumbs = {
      folders: [
        {
          name: "Home", // name of the folder
          uuid: "", // id of the folder - used to fetch its info again when needed
          data: [], // exactly same data as currentFolder (and previousFolder)
        },
      ],
    };

    // reset handlers, just in case
    this.recentFiles = [];

    this.navigationViewVisible = false;
    this.renamingFile = false;
    this.newFolderVisible = false;

    this.btnFolderBackVisible = false;
    this.btnFolderOperationsVisible = false;

    this.fileDetails = {
      filename: "",
      ext: "",
      upload_date: "",
      url: "",
      user: { full_name: "" },
      etag: "",
    };

    this.bulkActionSelectedFiles = [];
    this.bulkActionSelectedFolders = [];
  }

  loadFilesManagerHome() {
    // everything that needs to load
    // when initializing files manager
    if (this.recentFilesControl) {
      this.recentFilesControl.unsubscribe();
    }
    this.getRecentFiles();
    // this.getFileCounters();
  }

  getRecentFiles() {
    if (this.current_conversation.id == null) {
      return;
    }
    this.is_loading = true;
    // fetches the files featured in Recent files
    this.recentFilesControl = this.filesManagerService
      .getRecentFiles(this.current_conversation.id)
      .subscribe((res: any) => {
        const staff = res["staff"];
        const client = res["client"];

        if (staff.length >= 5 && client.length >= 5) {
          this.staffsFiles = staff.slice(0, 5);
          this.clientsFiles = client.slice(0, 5);
        } else {
          let slots = 10;
          if (staff.length < 5) {
            slots = slots - staff.length;
            this.staffsFiles = staff;
            this.clientsFiles = client.slice(0, slots - 1);
          } else {
            slots = slots - client.length;
            this.clientsFiles = client;
            this.staffsFiles = staff.slice(0, slots - 1);
          }
        }
        this.getFileCounters();
        this.is_loading = false;
        this.cdr.detectChanges();
      });
  }

  getFileCounters() {
    if (this.current_company.id == null) {
      return;
    }
    this.getFileCount("all");
    this.getFileCount("messages");
    this.getFileCount("tracked");
    this.getFileCount("staff");
    this.getFileCount("trash");
    this.getFileCount("easy-upload");
    this.getSignRequestCount();
  }

  // special files, theyre root level,
  // therefore they reset breadcrumbs

  getMessagesFiles() {
    // fetches files uploaded in conversations
    this.is_loading = true;
    this.selectedRecentFileIndex = 0;
    this.selectedFolderIndex = 1;
    this.lastFolderRetrieved = undefined;
    this.filesManagerService
      .getMessagesFiles(this.current_conversation.id, this.orderBy)
      .then((res: any) => {
        this.is_loading = false;
        const data = res;
        this.resetBreadcrumbs();

        this.appendBreadcrumb(data.title, "", data);
        this.currentFolder = data;
        this.navigationViewVisible = true;
        this.btnFolderBackVisible = false;
        this.btnFolderOperationsVisible = false;
      });
    this.fileViewVisible = false;
  }

  getTrackedItemsFiles() {
    // fetches files uploaded in client tasks
    this.is_loading = true;
    this.selectedRecentFileIndex = 0;
    this.selectedFolderIndex = 2;
    this.lastFolderRetrieved = undefined;
    this.filesManagerService
      .getTrackedItemsFiles(this.current_conversation.id, this.orderBy)
      .then((res: any) => {
        this.is_loading = false;
        const data = res;
        this.resetBreadcrumbs();
        this.bulkActionSelectedFiles = [];
        this.appendBreadcrumb(data.title, "", data);
        this.currentFolder = data;
        this.navigationViewVisible = true;
        this.btnFolderBackVisible = false;
        this.btnFolderOperationsVisible = false;
      });
    this.fileViewVisible = false;
  }

  // special folders - theyre root level,
  // therefore they reset breadcrumbs

  getStaffOnly() {
    // fetches folders that have staff_only = True
    this.is_loading = true;
    this.selectedRecentFileIndex = 0;
    this.selectedFolderIndex = 4;
    this.lastFolderRetrieved = undefined;
    this.filesManagerService
      .getStaffOnly(this.current_conversation.id, this.orderBy)
      .then((res: any) => {
        this.is_loading = false;
        const data = res;

        this.resetBreadcrumbs();
        this.bulkActionSelectedFiles = [];
        this.bulkActionSelectedFolders = [];
        this.appendBreadcrumb(data.title, "", data);
        this.currentFolder = data;
        this.navigationViewVisible = true;
        this.btnFolderBackVisible = true;
        this.btnFolderOperationsVisible = true;
        this.highlightFileInFolder();
        this.fileViewVisible = this.currentFolder.files.find(
          (o: any) => o.etag === this.fileDetails?.etag,
        )
          ? true
          : false;
        this.cdr.detectChanges();
      });
  }

  getClientShared() {
    if (this.current_conversation.id == null) {
      return;
    }

    // fetches all folders and files staff_only = False
    // and without parent folder (root level)
    this.is_loading = true;
    this.selectedRecentFileIndex = 0;
    this.selectedFolderIndex = 3;
    this.lastFolderRetrieved = undefined;
    this.filesManagerService
      .getAll(this.current_conversation.id, this.orderBy)
      .then((res: any) => {
        this.is_loading = false;
        const data = res;
        this.resetBreadcrumbs();
        this.bulkActionSelectedFiles = [];
        this.bulkActionSelectedFolders = [];
        this.appendBreadcrumb(data.title, "", data);
        this.currentFolder = data;
        this.navigationViewVisible = true;
        this.btnFolderBackVisible = true;
        this.btnFolderOperationsVisible = true;
        this.highlightFileInFolder();
        this.fileViewVisible = this.currentFolder.files.find(
          (o: any) => o.etag === this.fileDetails?.etag,
        )
          ? true
          : false;
        this.cdr.detectChanges();
      });

    this.cdr.detectChanges();
  }

  getTrash() {
    // fetches deleted files and folders
    this.is_loading = true;
    this.selectedRecentFileIndex = 0;
    this.selectedFolderIndex = 5;
    this.filesManagerService
      .getDeleted(this.current_conversation.id)
      .then((res: any) => {
        this.is_loading = false;
        const data = res;
        this.appendBreadcrumb(data.title, "", data);
        this.currentFolder = data;
        this.navigationViewVisible = true;
        this.btnFolderBackVisible = false;
        this.btnFolderOperationsVisible = false;
        this.cdr.detectChanges();
      });
    this.fileViewVisible = false;
  }

  getEasyUploadFiles() {
    // fetches files uploaded in conversations
    this.is_loading = true;
    this.selectedRecentFileIndex = 0;
    this.selectedFolderIndex = FOLDER_INDEX.EASY_UPLOAD_FILES;
    this.lastFolderRetrieved = undefined;
    this.filesManagerService
      .getEasyUploadFiles(this.current_conversation.id, this.orderBy)
      .then((res: any) => {
        this.is_loading = false;
        const data = res;
        this.resetBreadcrumbs();

        this.appendBreadcrumb(data.title, "", data);
        this.currentFolder = data;
        this.navigationViewVisible = true;
        this.btnFolderBackVisible = false;
        this.btnFolderOperationsVisible = false;
        this.cdr.detectChanges();
      });
    this.fileViewVisible = false;
  }

  getSignatureRequests() {
    this.is_loading = true;
    this.resetBreadcrumbs();
    this.selectedRecentFileIndex = 0;
    this.selectedFolderIndex = FOLDER_INDEX.E_SIGNATURE_FILES;
    this.filesManagerService
      .getSignatureRequests(this.current_conversation.id, this.orderBy)
      .subscribe(
        (res: any) => {
          this.appendBreadcrumb(res.title, "", res);
          this.currentFolder = res;
          this.signatureRequests = res.data;
          this.navigationViewVisible = true;
          this.btnFolderBackVisible = false;
          this.btnFolderOperationsVisible = false;
        },
        (err) => {},
        () => {
          this.is_loading = false;
          this.fileViewVisible = false;
        },
      );
  }

  getSignatureRequest(i: number, request_id: string) {
    this.selectSignatureRequest(null, i, Number(request_id));
    this.is_loading = true;
    this.lastFolderRetrieved = undefined;
    this.filesManagerService
      .getSignatureRequestDetails(this.current_conversation.id, request_id)
      .then((res: any) => {
        if (res.file) {
          this.filePreviewService.openFile.next({ file: res.file });
        } else if (res.document) {
          this.genericDrawerService.changeComponent(SignPreviewComponent, {
            mode: res.status,
            signRequest: res,
          });
          this.cdr.detectChanges();
        }
      })
      .catch((err) => {
        console.log({ err });
      })
      .finally(() => {
        this.is_loading = false;
        // refresh list
        this.getSignatureRequests();
      });
  }

  selectSignatureRequest(e: Event, i: number, request_id: number) {
    e?.stopPropagation();
    if (this.selectedFileIndex === i && this.showSignatureRequestDetails) {
      this.showSignatureRequestDetails = false;
      this.selectedFileIndex = 0;
      this.selectedSignatureRequest = null;
      return;
    }
    this.selectedFileIndex = i;
    this.showSignatureRequestDetails = true;
    this.selectedSignatureRequest = this.signatureRequests.find(
      (request: any) => request.id === request_id,
    );
  }

  confirmDocumentDelete() {
    this.modalService.show(DeleteConfirmationModalComponent, {
      initialState: {
        displayName: `the request to sign ${this.selectedSignatureRequest?.request_name}`,
      },
    }).content.onClose.subscribe((confirmed) => {
      if (confirmed) {
        this.deleteDocument();
      }
    }
    );
  }

  deleteDocument() {
    this.filesManagerService.deleteSignatureRequest(
        this.selectedSignatureRequest.conversation,
        this.selectedSignatureRequest.id).subscribe({
      next: (_) => {
        this.toastr.success("Document deleted successfully");
        this.genericDrawerService.closeDrawer$.next(null);
      },
      error: (err) => {
        this.toastr.error("Error deleting document");
        console.error("Error deleting document", err);
      }
    });
  }

  // folder handling - no backend

  reopenFolder(pos) {
    // reopens a folder already fetched from backend
    this.removeFromBreadcrumbs(pos + 1);
    this.currentFolder = this.breadcrumbs.folders[pos].data;
    if (pos === 0) {
      this.btnFolderBackVisible = false;
      this.btnFolderOperationsVisible = false;
      this.selectedFolderIndex = 0;
    }
  }

  // breadcrumb handling

  resetBreadcrumbs() {
    // resets breadcrumbs by removing all elements except first
    this.breadcrumbs.folders.length = 1;
    this.bulkActionSelectedFiles = [];
    this.bulkActionSelectedFolders = [];
  }

  appendBreadcrumb(name, uuid, data) {
    // add entry to breadcrumbs
    this.breadcrumbs.folders.push({ name, uuid, data });
    this.bulkActionSelectedFolders = [];
    this.bulkActionSelectedFiles = [];
  }

  removeFromBreadcrumbs(pos) {
    // discards all entries from array past given position
    this.breadcrumbs.folders.length = pos;
  }

  // retrieval

  getRecentFile(event: any, etag: string, highlight: number) {
    this.selectedRecentFileIndex = highlight;
    this.selectedFolderIndex = 0;
    this.navigationViewVisible = false;
    this.getFile(event, etag, 0);
  }

  handleEvent(event: any, etag: string): boolean {
    const className = "checked";

    if (!event || !event.target.classList.contains("file-icon")) {
      this.bulkActionSelectedFiles = [];
      this.elem.nativeElement
        .querySelectorAll(".checked")
        .forEach((ch) => this.renderer.removeClass(ch, className));
      return false;
    }

    if (event.target.parentNode.classList.contains(className)) {
      this.bulkActionSelectedFiles = this.bulkActionSelectedFiles.filter(
        (e) => e !== etag,
      );
      this.renderer.removeClass(event.target.parentNode, className);
      this.selectedRecentFileIndex = 0;
    } else {
      this.bulkActionSelectedFiles.push(etag);
      this.renderer.addClass(event.target.parentNode, className);
    }
    return true;
  }

  handleEventForFolder(event: any, folder_uuid: string): boolean {
    const className = "checked";

    if (!event || !event.target.classList.contains("file-icon")) {
      this.bulkActionSelectedFolders = [];
      this.elem.nativeElement
        .querySelectorAll(".checked")
        .forEach((ch) => this.renderer.removeClass(ch, className));
      return false;
    }

    if (event.target.parentNode.classList.contains(className)) {
      this.bulkActionSelectedFolders = this.bulkActionSelectedFolders.filter(
        (uuid) => uuid !== folder_uuid,
      );
      this.renderer.removeClass(event.target.parentNode, className);
      this.selectedFolderIndex = 0;
    } else {
      this.bulkActionSelectedFolders.push(folder_uuid);

      this.renderer.addClass(event.target.parentNode, className);
    }
    return true;
  }

  getFile(event: any, etag: string, highlight: number) {
    const isSelecting = this.handleEvent(event, etag);
    if (isSelecting) {
      return;
    }

    // retrieve a file by a given etag
    this.is_loading = true;
    this.selectedFileIndex = highlight;
    this.renamingFile = false;

    this.filesManagerService
      .getFileDetails(this.current_conversation.id, etag)
      .then((res: any) => {
        this.is_loading = false;
        const data = res;
        this.fileDetails = data["file"];
        // this.fileViewVisible = true;

        this.filePreviewService.openFile.next({ file: data.file });
      });
  }

  getFolder(folder_uuid: string, event: any = null) {
    if (this.current_data.access_level == 1) {
      const isSelecting = this.handleEventForFolder(event, folder_uuid);

      if (isSelecting) {
        return;
      }
    }
    // retrieve a folder by a given uuid
    this.is_loading = true;
    this.lastFolderRetrieved = folder_uuid;
    this.filesManagerService
      .getFolder(this.current_conversation.id, folder_uuid, this.orderBy)
      .then((res: any) => {
        this.is_loading = false;
        const data = res;
        console.log("getFolder");
        console.log(data);
        if (
          this.breadcrumbs.folders[this.breadcrumbs.folders.length - 1].uuid ===
          folder_uuid
        ) {
          this.bulkActionSelectedFiles = [];
          this.bulkActionSelectedFolders = [];

          this.breadcrumbs.folders.pop();

          // return;
        }
        this.breadcrumbs.folders.push({
          name: data.title,
          uuid: folder_uuid,
          data,
        });
        this.currentFolder = data;
        this.cdr.detectChanges();
      });
    this.fileViewVisible = false;
  }

  // actions

  async getFileCount(type) {
    if (this.current_conversation.id == null) {
      return;
    }

    this.is_loading = true;
    await this.filesManagerService
      .getFileCount(this.current_conversation.id, type)
      .then((res: any) => {
        this.is_loading = false;
        const data = res;
        type == "easy-upload" ? (type = "easyUpload") : type; // fix for easy-upload to easyUpload
        this.fileCounters[type] = data.files;
        this.folderCounters[type] = data.folders;
      });
  }

  async getSignRequestCount() {
    this.fileCounters.eSign = (
      await this.filesManagerService.getSignRequestCount(
        this.current_conversation.id,
      )
    ).count;
  }

  safeUrl(etag: string) {
    return `${environment.apiUrl}/conversations/file/${etag}/private`;
  }

  folderBackButton() {
    // navigates to previous folder (back arrow in middle column)
    const pos = this.breadcrumbs.folders.length - 1;
    this.currentFolder = this.breadcrumbs.folders[pos - 1].data;
    this.removeFromBreadcrumbs(pos);
    this.reload();
    this.newFolderVisible = false;
    this.bulkActionSelectedFiles = [];
    this.bulkActionSelectedFolders = [];

    if (pos === 1) {
      // last item. hide gui
      this.btnFolderBackVisible = false;
      this.btnFolderOperationsVisible = false;
      this.selectedFolderIndex = 0;
      this.selectedFileIndex = 0;
      this.fileViewVisible = false;
    }
  }

  renameFile(uuid, name) {
    this.is_loading = true;
    this.filesManagerService
      .renameFile(this.current_conversation.id, uuid, name)
      .then(
        (result) => {
          this.is_loading = false;

          this.renamingFile = false;
          this.toastr.success("File renamed successfully.");
          this.reload();
          this.getRecentFiles();
          this.getFile(null, uuid, this.selectedFileIndex);
        },
        (error) => {
          this.is_loading = false;
          this.renamingFile = false;
          this.toastr.error("Something went wrong when renaming the file.");
        },
      );
  }

  deleteFile(uuid: string) {
    this.is_loading = true;
    this.filesManagerService
      .deleteFile(this.current_conversation.id, uuid)
      .then(
        (result) => {
          this.is_loading = false;
          console.log("successfully deleted");
          this.toastr.success("File deleted successfully.");
          this.reload();
          this.loadFilesManagerHome();
          this.bsDeleteModalRef.hide();

          // file deleted, dont select anything
          this.selectedFileIndex = 0;
        },
        (error) => {
          this.is_loading = false;
          console.log("delete failed");
          this.toastr.error("Something went wrong when deleting the file.");
          this.bsDeleteModalRef.hide();
        },
      );
  }

  createFolder(
    name: string,
    parentFolderUUID: string = this.currentFolder.uuid,
  ) {
    this.is_loading = true;
    const parentUUID = parentFolderUUID;
    const isStaffOnly = this.selectedFolderIndex === 4;
    console.log("uuid,staff", parentUUID, isStaffOnly);
    this.filesManagerService
      .createFolder(this.current_conversation.id, parentUUID, name, isStaffOnly)
      .then(
        (result) => {
          this.is_loading = false;
          console.log("successfully created");
          const className = "checked";
          this.toastr.success("Folder created successfully.");
          this.newFolderVisible = false;
          this.txtNewFolder.nativeElement.value = "";
          if (parentUUID == RootFolders.ROOT_CLIENT_SHARED) {
            this.getClientShared();
          } else if (parentUUID == RootFolders.ROOT_STAFF_ONLY) {
            this.getStaffOnly();
          } else {
            this.getFolder(parentUUID);
          }
        },
        (error) => {
          this.is_loading = false;
          console.log("failed to create");
          this.toastr.error("Something went wrong when creating the folder.");
          this.newFolderVisible = false;
        },
      );
    console.log("create a child of ", parentUUID);
  }

  renameFolder(uuid: string, name: string) {
    console.log("renaming folder", this.currentFolder.title, "to", name);
    console.log("uuid", uuid);
    this.is_loading = true;
    this.filesManagerService
      .renameFolder(this.current_conversation.id, uuid, name)
      .then(
        (result) => {
          this.is_loading = false;
          console.log("successfully renamed");
          this.reload();
          this.toastr.success("Folder renamed successfully.");
          this.renameFolderVisible = false;
        },
        (error) => {
          this.is_loading = false;
          console.log("failed to rename");
          this.toastr.error("Something went wrong when renaming the folder.");
          this.renameFolderVisible = false;
        },
      );
  }

  deleteFolder(uuid: string) {
    // once we delete the folder, we must go back 1 folder
    this.is_loading = true;
    console.log("deleting folder", uuid);
    this.filesManagerService
      .deleteFolder(this.current_conversation.id, uuid)
      .then(
        (success) => {
          this.folderBackButton();
          this.reload();
          this.is_loading = false;
          console.log("successfully deleted");
          this.toastr.success(
            `Folder ${this.currentFolder.title} deleted successfully.`,
          );
        },
        (error) => {
          this.is_loading = false;
          console.log("failed to create");
          this.toastr.error("Something went wrong when deleting the folder.");
        },
      );
  }

  reload() {
    // reloads open folder
    if (
      this.currentFolder.uuid != RootFolders.ROOT_CLIENT_SHARED &&
      this.currentFolder.uuid != RootFolders.ROOT_STAFF_ONLY
    ) {
      this.getFolder(this.currentFolder.uuid);
    } else {
      if (this.selectedFolderIndex === 3) {
        this.getClientShared();
      } else if (this.selectedFolderIndex === 4) {
        this.getStaffOnly();
      } else {
        console.log("couldnt decide which folder to reload");
        console.log("selected folder was", this.selectedFolderIndex);
      }
    }
  }

  reloadAfterUpload() {
    // reloads open folder
    if (
      this.currentFolder.uuid != RootFolders.ROOT_CLIENT_SHARED &&
      this.currentFolder.uuid != RootFolders.ROOT_STAFF_ONLY
    ) {
      this.getFolder(this.currentFolder.uuid);
    }
  }

  downloadFile() {
    this.filesManagerService.download(this.fileDetails.etag);
  }

  downloadByEtag(etag: string) {
    this.filesManagerService.download(etag);
  }

  downloadSelected() {
    this.bulkActionSelectedFiles.forEach((etag) => this.downloadByEtag(etag));
  }

  deleteSelected() {
    let displayContent;
    if (this.bulkActionSelectedFiles.length > 1) {
      displayContent = `${this.bulkActionSelectedFiles.length} files`;
    } else {
      displayContent = `${this.bulkActionSelectedFiles.length} file`;
    }
    const initialState = {
      displayName: displayContent,
    };

    const deleteModal = this.modalService.show(
      DeleteConfirmationModalComponent,
      { initialState },
    );

    deleteModal.content.onClose.subscribe((confirmed) => {
      if (confirmed) {
        let uuids_array = [];
        this.bulkActionSelectedFiles.forEach((uuid) => uuids_array.push(uuid));
        this.filesManagerService
          .deleteSelectedFiles(this.current_conversation.id, uuids_array)
          .then((res) => {
            console.log(res);
            this.bulkActionSelectedFiles.forEach((etag) => {
              this.currentFolder.files = this.currentFolder.files.filter(
                (file) => file.etag != etag,
              );
            });
            this.bulkActionSelectedFiles = [];
            this.getRecentFiles();
          });
        this.toastr.success("File(s) deleted successfully!");
      }
    });
  }

  zipAndDownloadFolderFiles(): void {
    const formattedTitle = this.currentFolder.title.replace(/ /g, "_");
    this.dashboardService.show_loading.next(true);
    this.filesManagerService
      .zipAndDownloadFolderFiles(this.currentFolder.uuid)
      .then((blob) => {
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        document.body.appendChild(a);
        a.style.display = "none";
        a.href = downloadUrl;
        a.download = `${formattedTitle}_files.zip`;
        a.click();
        URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
        this.dashboardService.show_loading.next(false);
      });
  }

  downloadAllContentFromFolders() {
    this.dashboardService.show_loading.next(true);
    this.filesManagerService
      .downloadFoldersContent(this.current_conversation.id)
      .then((blob: any) => {
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        document.body.appendChild(a);
        a.style.display = "none";
        a.href = downloadUrl;
        a.download = `File-Manager-Folders-Content.zip`;
        a.click();
        URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
        this.dashboardService.show_loading.next(false);
      });
  }

  bulkSelectUnselectAllFiles(action: string) {
    const className = "checked";
    const filesElements = this.fileElements.toArray();

    if (action === "select") {
      filesElements.forEach((element: ElementRef) => {
        if (!element.nativeElement.classList.contains(className)) {
          this.renderer.addClass(element.nativeElement, className);
        }
      });
      this.bulkActionSelectedFiles = this.currentFolder.files.map(
        (file) => file.etag,
      );
    }
    if (action === "unselect") {
      filesElements.forEach((element) => {
        if (element.nativeElement.classList.contains(className)) {
          this.renderer.removeClass(element.nativeElement, className);
        }
      });
      this.bulkActionSelectedFiles = [];
    }
  }

  checkClientForDeleteFile(): boolean {
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const { user } = this.current_data;
    const files = this.currentFolder.files.filter((file) =>
      this.bulkActionSelectedFiles.includes(file.etag),
    );
    if (
      files.every(
        (file) =>
          file.sent_by.id === user.id &&
          new Date(file.upload_date) >= twentyFourHoursAgo,
      )
    ) {
      return true;
    }
    return false;
  }

  /**************************************************************************
    DRAG AND DROP TO UPLOAD STARTS HERE
  **************************************************************************/

  // When dragging over current folder, can drop anywhere in the middle column
  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDraggingFileOverSubfolder = true;
    this.isDraggingFileOverCurrentFolder = true;
    this.uploadFolderName = this.currentFolder.title;
  }

  // When drag leave current folder (anywhere inside middle panel)
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDraggingFileOverSubfolder = false;
    this.isDraggingFileOverCurrentFolder = false;
  }

  // When drop a file over current folder (anywhere inside middle panel)
  onFileDrop(event: DragEvent, target = undefined) {
    event.preventDefault();
    this.isDraggingFileOverCurrentFolder = false;
    this.isDraggingFileOverSubfolder = false;
    event.stopPropagation();
    if (
      ["Easy Upload Files", "Messages Files", "Client Tasks Files"].includes(
        target,
      )
    ) {
      this.toastr.error(
        `Drag and drop is only supported to put files into folders. These buckets files are automatically populated from other parts of Client Hub.`,
      );
      return;
    }
    (event.target as HTMLElement).classList.remove("dragging-file");
    const items = event.dataTransfer.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i].webkitGetAsEntry();
      if (item.name !== ".DS_Store") {
        if (item.isFile) {
          // FILE
          this.uploadFile(item, this.currentFolder);
        } else if (item.isDirectory) {
          // DIRECTORY
          this.readDirectory(item, this.currentFolder);
        }
      }
    }
  }

  // When dragging over a subfolder
  onDragOverFolder(event: DragEvent, folder) {
    event.preventDefault();
    this.isDraggingFileOverSubfolder = true;
    event.stopPropagation();
    (event.target as HTMLElement).classList.add("dragging-file");
    this.uploadFolderName = folder.name;
  }

  // When drag leave a subfolder
  onDragLeaveFolder(event: DragEvent) {
    event.preventDefault();
    this.isDraggingFileOverSubfolder = false;
    this.uploadFolderName = "";
    event.stopPropagation();
    (event.target as HTMLElement).classList.remove("dragging-file");
  }

  // When drop a file inside a subfolder
  onFileDropFolder(event: DragEvent, folder) {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFileOverSubfolder = false;
    (event.target as HTMLElement).classList.remove("dragging-file");
    const items = event.dataTransfer.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i].webkitGetAsEntry();
      if (item.name !== ".DS_Store") {
        if (item.isFile) {
          // FILE
          this.uploadFile(item, folder, folder.name);
        } else if (item.isDirectory) {
          // DIRECTORY
          this.readDirectory(item, folder);
        }
      }
    }
  }

  // Read the contents of the uploaded folder, create subfolders and upload files
  readDirectory(directory, folder, groupFolder = null) {
    const dirReader = directory.createReader();
    const selectedFolder = folder;
    const isStaffOnly = this.selectedFolderIndex === 4;

    this.filesManagerService
      .createFolder(
        this.current_conversation.id,
        selectedFolder.uuid,
        directory.name,
        isStaffOnly,
      )
      .then(
        (res) => {
          const createdFolder = {
            uuid: res.folder_uuid,
          };
          // Upload files inside that folder
          dirReader.readEntries(
            (items: any[]) => {
              // Loop through the files/folders inside the folder
              for (let i = 0; i < items.length; i++) {
                const item = items[i];
                console.log("FILE", item);

                if (item.isFile) {
                  // If its a file upload it
                  this.uploadFile(
                    item,
                    createdFolder,
                    groupFolder || directory.name,
                  );
                } else if (item.isDirectory) {
                  // If its a folder recursively explore subdirectories
                  this.readDirectory(
                    item,
                    createdFolder,
                    groupFolder || directory.name,
                  );
                }
              }
            },
            (error: any) => {
              console.error("Error reading directory:", error);
            },
          );
        },
        (error) => {
          this.toastr.error(
            `Something went wrong when creating the folder ${name}.`,
          );
        },
      );
  }

  // Upload a file
  uploadFile(item, folder, groupFolder = null) {
    item.file((file: File) => {
      if (file.name !== ".DS_Store") {
        let formData = new FormData();
        formData.append("files", file, file.name);
        let generatedId =
          Date.now().toString(36) + Math.random().toString(36).substring(2);
        this.uploadList.push({
          id: generatedId,
          filename: file.name,
          progress: 0,
          status: 0,
          folder: item.fullPath,
          group: groupFolder || this.currentFolder.title,
        });

        // Upload and progressbar
        this.filesManagerService
          .newUploadFiles(
            this.current_conversation.id,
            folder.uuid,
            this.selectedFolderIndex === 4,
            formData,
          )
          .subscribe(
            (res: any) => {
              console.log("Progress", res);
              if (res?.status === "progress") {
                this.uploadListOpen = true;
                let progress = res?.message;
                Object.keys(this.uploadList).forEach((item) => {
                  if (this.uploadList[item].id === generatedId) {
                    this.uploadList[item].progress = progress;
                  }
                });
                this.cdr.detectChanges();
                this.warningUploadInProgress = true; // Warn user if left page before upload finishes
              }
              if (res?.status === "completed") {
                this.totalUploadedFiles < this.uploadList.length
                  ? this.totalUploadedFiles++
                  : this.uploadList.length;
                let status = res?.status;
                Object.keys(this.uploadList).forEach((item) => {
                  if (this.uploadList[item].id === generatedId) {
                    this.uploadList[item].status = status;
                  }
                });
                // All files uploaded, update view with new files/folders
                if (
                  this.uploadList.every(
                    (upload) => upload["status"] === "completed",
                  )
                ) {
                  this.warningUploadInProgress = false;
                  this.reload();
                  this.loadFilesManagerHome();
                  this.cdr.detectChanges();
                }

                this.cdr.detectChanges();
              }
            },
            (error: any) => {
              console.log(error);
              let errorMsg = error.error.message
                ? error.error.message
                : `Something went wrong when uploading ${file.name}.`;
              this.toastr.error(errorMsg);
              this.uploadErrors++;
              Object.keys(this.uploadList).forEach((item) => {
                if (this.uploadList[item].id === generatedId) {
                  this.uploadList[item].progress = "error";
                  this.uploadList[item].errorMsg = errorMsg;
                }
              });
              this.warningUploadInProgress = true;
            },
            () => {
              this.cdr.detectChanges();
            },
          );
      }
    });
  }

  // Count uploaded items
  countItems(groupName: string, progressValue: number): number {
    return this.uploadList.filter(
      (item) => item.group === groupName && item.progress === progressValue,
    ).length;
  }

  /**************************************************************************
    CHANGE VIEW STARTS HERE
  **************************************************************************/

  changeView(view: string, listFilteringType: string = undefined) {
    if (listFilteringType) this.listFiltering = listFilteringType;
    this.localStorage.set("preferredView", view);
    this.view = view;
  }

  changeListFiltering(listFilteringType: string) {
    this.localStorage.set("preferredListFiltering", listFilteringType);
    this.listFiltering = listFilteringType;
  }

  changeOrderBy(orderBy: string, orderByLabel: string) {
    this.orderBy = orderBy;
    this.localStorage.set("preferredOrderBy", orderBy);
    this.orderByLabel = orderByLabel;
    this.localStorage.set("preferredOrderByLabel", orderByLabel);

    // re-load folder
    if (this.lastFolderRetrieved) {
      this.getFolder(this.lastFolderRetrieved);
    } else {
      this[this.folderIndexMapping[this.selectedFolderIndex]]();
    }
  }
}
